/**
 * Upload a feature-demo video (or poster image) for a notification.
 *
 * This is the admin-self-serve half of the adoption work: an admin records a
 * walkthrough in whatever tool they prefer (Guidde, Supademo, a screen
 * recorder), exports an MP4, and attaches it here — no developer, no deploy.
 *
 * The admin never types a URL. The backend mints a presigned PUT and hands back
 * the `mediaPath` to store, which is what keeps the notification's media field
 * restricted to our own bucket (an arbitrary URL there would become a `src` in
 * every targeted client).
 */

import React, { useRef, useState } from 'react';
import { Box, Button, LinearProgress, Typography, IconButton, Alert } from '@mui/material';
import { CloudUpload as CloudUploadIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { adminApi } from '../services/api';
import { VIDEO_ACCEPT, IMAGE_ACCEPT } from '../constants/notificationMediaTypes';
import { checkMp4Faststart } from '../utils/mp4Faststart';

/** Mirrors the backend's MAX_MEDIA_BYTES. */
const MAX_BYTES = 100 * 1024 * 1024;

interface NotificationMediaUploadProps {
  label: string;
  helperText: string;
  /** Current stored media path, or '' when none. */
  value: string;
  onChange: (mediaPath: string) => void;
  /** Restrict the picker to video or image types. */
  kind: 'video' | 'image';
  testIdPrefix: string;
}

// Derived from the same list api.ts validates against — see
// constants/notificationMediaTypes.ts for why they can't be written twice.

const NotificationMediaUpload: React.FC<NotificationMediaUploadProps> = ({
  label,
  helperText,
  value,
  onChange,
  kind,
  testIdPrefix,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [playbackWarning, setPlaybackWarning] = useState<string | null>(null);

  const handleFile = async (file: File) => {
    setError(null);
    setPlaybackWarning(null);

    // Checked here as well as server-side so an admin who picked a 400 MB
    // screen recording finds out immediately rather than after the upload.
    if (file.size > MAX_BYTES) {
      setError(`That file is ${(file.size / 1024 / 1024).toFixed(0)} MB — the limit is 100 MB. Trim the clip or export it smaller.`);
      return;
    }

    // A recorder that writes its index last produces a file the browser must
    // download ENTIRELY before showing a frame — the play button then looks
    // dead rather than erroring, which is how this surfaced in practice. Warn
    // rather than block: the upload is valid, and the admin may have no way to
    // re-export right now.
    let warning: string | null = null;
    if (kind === 'video' && file.name.toLowerCase().endsWith('.mp4')) {
      try {
        const { faststart, parsed } = await checkMp4Faststart(file);
        const mb = file.size / 1024 / 1024;
        if (parsed && !faststart) {
          warning =
            `This clip only starts playing once the whole ${mb.toFixed(0)} MB has downloaded, so the play button ` +
            'will look broken to clients. Re-export it with "faststart" / "optimise for web", or run:\n' +
            'ffmpeg -i in.mp4 -movflags +faststart -vf scale=1280:-2 -crf 28 out.mp4';
        } else if (mb > 20) {
          warning = `That's ${mb.toFixed(0)} MB — it will play, but slowly on a phone. A short demo is usually 1-5 MB.`;
        }
      } catch {
        // A scan failure must never block a valid upload.
      }
    }

    try {
      setUploading(true);
      setProgress(0);
      const mediaPath = await adminApi.uploadNotificationMedia(file, setProgress);
      onChange(mediaPath);
      setPlaybackWarning(warning);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Upload failed');
    } finally {
      setUploading(false);
      // Clear the input so re-picking the same file fires onChange again.
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <Box data-testid={`${testIdPrefix}-block`}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
        {label}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        {helperText}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 1 }} data-testid={`${testIdPrefix}-error`}>
          {error}
        </Alert>
      )}

      {playbackWarning && (
        <Alert
          severity="warning"
          sx={{ mb: 1, whiteSpace: 'pre-line' }}
          data-testid={`${testIdPrefix}-playback-warning`}
          onClose={() => setPlaybackWarning(null)}
        >
          {playbackWarning}
        </Alert>
      )}

      {value ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" sx={{ flex: 1, wordBreak: 'break-all' }} data-testid={`${testIdPrefix}-path`}>
            {value}
          </Typography>
          <IconButton
            size="small"
            aria-label={`Remove ${label}`}
            onClick={() => onChange('')}
            data-testid={`${testIdPrefix}-remove`}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ) : (
        <Button
          component="label"
          variant="outlined"
          size="small"
          startIcon={<CloudUploadIcon />}
          disabled={uploading}
          data-testid={`${testIdPrefix}-button`}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          {uploading ? 'Uploading…' : `Upload ${kind}`}
          <input
            ref={inputRef}
            type="file"
            hidden
            accept={kind === 'video' ? VIDEO_ACCEPT : IMAGE_ACCEPT}
            data-testid={`${testIdPrefix}-input`}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void handleFile(file);
            }}
          />
        </Button>
      )}

      {uploading && (
        <Box sx={{ mt: 1 }}>
          <LinearProgress variant="determinate" value={progress} data-testid={`${testIdPrefix}-progress`} />
          <Typography variant="caption" color="text.secondary">
            {progress}%
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export { MAX_BYTES };
export default NotificationMediaUpload;
